import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button } from 'reactstrap';
import { useAlertPlus } from '../../../hooks';
import AlertModal from '../../../components/AlertModal';
import Form from './Form';
import config from 'config';
import { ApiService } from '../../../services';
import { getPreviewSrc, encodeFileName } from '../../../helpers/filesPreview';

import './Edit.scss';

export interface FacturacionOOSSRequest {
  matriculate: {
    id: string | undefined;
    nombre: string;
    apellido: string;
    delegacion: string;
  };

  atencion_domiciliario: boolean;
  atencion_domiciliario_ioma: boolean;
  id_localidad: number;
  prefijo_telefono_domicilio: string;
  numero_telefono_domicilio: string;
  numero_rnp: string;
  vencimiento_rnp: string;
  prestador_os: boolean;
  prestador_ioma: boolean;
  condicion_iva: string;
  nro_sucursal_banco: string;
  nombre_sucursal_banco: string;

  localidad_name: string;
  partido_name: string;

  dgr_matriculado: string;
  motivo_rechazo: string;

  state_id: string;

  consultorios: FacturacionOOSSConsultorio[];
  attachs: RequestAttachment[];
}

export interface FacturacionOOSSConsultorio {
  id: number | null;
  request_id: number | null;
  es_habilitado: boolean | null;
  consultorio_id: number | null;
  numero_resolucion: string | null;
  lugar_atencion_id: number | null;
  calle: string | null;
  numero: string | null;
  piso: string | null;
  departamento: string | null;
  id_localidad: number | null;
  codigo_postal: string | null;
  prefijo_telefono: string | null;
  telefono: string | null;
  horarios_atencion: HorariosAtencion[] | null;
}

export interface HorariosAtencion {
  horaInicio: string;
  horaFin: string;
  diaSemana: string;
}

export interface RequestAttachment {
  id: number;
  request_id: number;
  request_type: string;
  attachment_type: string;
  url: string;
  created_at: string;
}

export interface FacturacionOOSSRequestAttachs {
  tipo: string;
  tipo_archivo: string;
  url: string;
}

export interface GalleryItem {
  src: string;
  downloadUrl: string;
  alt: string;
}

function Edit() {
  const { id } = useParams();

  const [data, setData] = useState<FacturacionOOSSRequest>({
    matriculate: {
      id: id,
      nombre: '',
      apellido: '',
      delegacion: '',
    },

    atencion_domiciliario: false,
    atencion_domiciliario_ioma: false,
    id_localidad: 0,
    prefijo_telefono_domicilio: '',
    numero_telefono_domicilio: '',
    numero_rnp: '',
    vencimiento_rnp: '',
    prestador_os: false,
    prestador_ioma: false,
    condicion_iva: '',
    nro_sucursal_banco: '',
    nombre_sucursal_banco: '',

    localidad_name: '',
    partido_name: '',

    dgr_matriculado: '',
    motivo_rechazo: '',

    state_id: '',

    consultorios: [],
    attachs: [],
  });

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const galleryContainer = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const handleSend = async (accept = false) => {
    try {
      setIsLoading(true);

      // Preparamos los datos
      const formData = new FormData();
      formData.append('dgr_matriculado', data.dgr_matriculado ?? '');
      formData.append('motivo_rechazo', data.motivo_rechazo ?? '');

      const headers: HeadersInit = {
        ...(localStorage.getItem('accessToken') && {
          Authorization: localStorage.getItem('accessToken') as string,
        }),
      };

      const res = await fetch(
        `${config.baseUrl}/matriculates/facturacion-ooss-request/${ id }/${ accept ? 'accept' : 'reject' }`,
        {
          method: 'POST',
          headers,
          body: formData,
        },
      );

      const response = await res.json();

      if (response?.status !== 'ok') {
        if (response.message) {
          alert(response.message);
          return;
        }

        alert('Ha ocurrido un error al enviar al servidor, por favor intente nuevamente.');
        return;
      }

      alert('Se guardó correctamente la resolución.', () => {
        window.location.href = '/admin/matriculates/requests';
      });
    } catch (err) {
      console.error(err);
      alert('Ha ocurrido un error al guardar la resolución, por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const ATTACHMENTS_CONFIG = [
    { key: 'registro_prestadores', alt: 'Registro Nacional de Prestadores' },
    { key: 'constancia_afip', alt: 'Constancia Situación Ante AFIP' },
    { key: 'inscripcion_iibb', alt: 'Inscripción IIBB ARBA' },
    { key: 'adhesion_ioma', alt: 'Planilla Adheción IOMA' },
    { key: 'declaracion_consultorio', alt: 'Declaración de Consultorio' },
  ] as const;

  useEffect(() => {
    ApiService.getOne('matriculates/facturacion-ooss-request', id)
      .then(({ payload }) => {
        setData(payload);

        const attachs = ATTACHMENTS_CONFIG.flatMap(({ key, alt }) => {
          if (!payload.attachs?.length) return [];

          const filtered = payload.attachs
            .filter((a: RequestAttachment) => a.attachment_type.startsWith(key))
            .sort((a: RequestAttachment, b: RequestAttachment) => {
              const indexA = parseInt(a.attachment_type.split('_').pop() || '0');
              const indexB = parseInt(b.attachment_type.split('_').pop() || '0');
              return indexA - indexB;
            });

          let i = 0;
          return filtered.map((a: RequestAttachment) => {
            const encodedPath = encodeFileName(a.url);
            i++;

            return {
              src: getPreviewSrc(encodedPath),
              downloadUrl: `${config.baseUrl}/${encodedPath}`,
              // @ts-ignore
              alt: `${alt} ${i}`,
            };
          });
        });

        setGallery(attachs);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Card className="bg-secondary shadow">
          <CardHeader>
            <Row>
              <Col md="8">
                <h3 className="mb-0">Solicitud de Facturación en OOSS #{ id }</h3>
              </Col>
              <Col md="4" className="text-right">
                <Link to='/admin/matriculates/requests'>
                  <Button color="primary" size="md">Volver</Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form
              data={ data }
              setData={ setData }
              gallery={ gallery }
              galleryContainer={ galleryContainer }
              isLoading={ isLoading }
              onAccept={ () => handleSend(true) }
              onReject={ () => handleSend(false) }
            />
          </CardBody>
        </Card>
      </Container>
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
    </>
  );
}

export default Edit;
